/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import Styles from '../../css/LoanInformation.module.css';
import { useLoanInformationContext } from '../../contexts/LoanInformationContext';
import EnvelopeWithBox from '../EnvelopeWithBox';
import { getTimestampAsDate } from '../../common/UtilityFunctions';
import { useCallback } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';

const SystemFlags = () => {
  const {
    propData,
    handlePropChange,
    servicingData,
    handleServicingChange,
    loanData,
    handleLoanChange,
    loanEvents,
    borrowersArr,
    originalLoanData,
  } = useLoanInformationContext();

  const { fullAccessSuperUser } = useAuthContext();

  const handleTinTypeChange = useCallback(
    (newTinType: string) => {
      if (newTinType === '') {
        handlePropChange('edit', 'vestHeldInEntity', false);
        handlePropChange('edit', 'investmentPurpose', false);
      } else {
        const newVestHeldInEntity = newTinType == '1 - Business';
        const newInvestmentPurpose = newTinType == '1 - Business';
        handlePropChange('edit', 'vestHeldInEntity', newVestHeldInEntity);
        handlePropChange('edit', 'investmentPurpose', newInvestmentPurpose);
      }
    },
    [handlePropChange],
  );

  return (
    <EnvelopeWithBox blockName='System Flags'>
      <div className={Styles.loanInfoTable}>
        <div className={Styles.leftColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Suppress Notices: </th>
                <td>
                  <select
                    value={loanData?.suppressNotice ? 'Yes' : 'No'}
                    onChange={(e) => handleLoanChange('edit', 'suppressNotice', e.target.value == 'Yes' ? true : false)}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    <option value='Yes'>Yes</option>
                    <option value='No'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Send Statement:</th>
                <td>
                  <select
                    name='dontSendStatement'
                    value={String(servicingData?.dontSendStatement)}
                    onChange={(e) => handleServicingChange('edit', 'dontSendStatement', e.target.value === 'true')}
                  >
                    <option value='false'>Yes</option>
                    <option value='true'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Suspend Letters:</th>
                <td>
                  <select
                    name='suspendLetters'
                    value={servicingData?.suspendLetters ? 'true' : 'false'}
                    onChange={(e) => handleServicingChange('edit', 'suspendLetters', e.target.value === 'true')}
                  >
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>PIF Reason: </th>
                <td>{loanData?.PIFReason ? `${loanData?.PIFReason} - ${loanData?.PIFReasonDescription}` : ' '}</td>
              </tr>
              <tr>
                <th>Assumable: </th>
                <td>
                  <select
                    value={loanData?.assumable ? 'Yes' : 'No'}
                    disabled={!fullAccessSuperUser}
                    onChange={(e) => {
                      const newval = e.target.value == 'Yes' ? true : false;
                      handleLoanChange('edit', 'assumable', newval);
                      if (originalLoanData?.assumable == newval) {
                        handleLoanChange('cancel', 'depProtectAssumable');
                      } else {
                        handleLoanChange('edit', 'depProtectAssumable', false);
                      }
                    }}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    <option value='Yes'>Yes</option>
                    <option value='No'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Units: </th>
                <td>{loanData?.propertyNumUnits ? loanData?.propertyNumUnits : 0}</td>
              </tr>
              <tr>
                <th>Balloon: </th>
                <td>{loanData?.balloonPayment ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <th>Prepay Penalty: </th>
                <td>
                  <select
                    value={loanData?.prepaymentPenalty ? 'Yes' : 'No'}
                    disabled={!fullAccessSuperUser}
                    onChange={(e) => {
                      const newval = e.target.value == 'Yes' ? true : false;
                      handleLoanChange('edit', 'prepaymentPenalty', newval);
                      if (originalLoanData?.prepaymentPenalty == newval) {
                        handleLoanChange('cancel', 'depProtectPrepaymentPenalty');
                      } else {
                        handleLoanChange('edit', 'depProtectPrepaymentPenalty', false);
                      }
                    }}
                  >
                    <option value='' disabled>
                      -- Select an Option --
                    </option>
                    <option value='Yes'>Yes</option>
                    <option value='No'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Payment Frequency: </th>
                <td>{loanData?.pmtClctFreqDescription ? loanData?.pmtClctFreqDescription : ''}</td>
              </tr>
              <tr>
                <th>High-Priced ML: </th>
                <td>{loanData?.hpml ? 'Yes' : 'No'}</td>
              </tr>
              <tr>
                <td colSpan={2} style={{ height: '20px' }}></td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={Styles.rightColumn}>
          <table className={Styles.loanInfoTableSection}>
            <tbody>
              <tr>
                <th>Tin Type:</th>
                <td>
                  <select
                    name='tinType'
                    value={
                      propData
                        ? propData?.vestHeldInEntity && propData?.investmentPurpose
                          ? '1 - Business'
                          : '2 - Individual'
                        : ''
                    }
                    onChange={(e) => handleTinTypeChange(e.target.value)}
                  >
                    <option value=''>Select Tin Type</option>
                    <option value='1 - Business'>1 - Business</option>
                    <option value='2 - Individual'>2 - Individual</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Repurchase:</th>
                <td>
                  <select
                    name='repurchase'
                    value={servicingData?.repurchase ? 'true' : 'false'}
                    onChange={(e) => handleServicingChange('edit', 'repurchase', e.target.value === 'true')}
                  >
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>AEA Audit:</th>
                <td>
                  <select
                    name='aeaAudit'
                    value={servicingData?.aeaAudit ? 'true' : 'false'}
                    onChange={(e) => handleServicingChange('edit', 'aeaAudit', e.target.value === 'true')}
                  >
                    <option value='true'>Yes</option>
                    <option value='false'>No</option>
                  </select>
                </td>
              </tr>
              <tr>
                <th>Vacant Date:</th>
                <td>{loanEvents?.SRM ? getTimestampAsDate(loanEvents?.SRM?.activeDate) : ''}</td>
              </tr>
              <tr>
                <th>Conforming: </th>
                <td>{loanData?.loanConforming ? 'Yes' : 'No'}</td>
              </tr>
              {borrowersArr
                .filter((borrower) => borrower.borrowerOnNote && borrower.borrowerTypeKeyword !== 'REMOVED')
                .map((borrower, idx) => (
                  <tr key={`employee-${borrower.borrowerNumber}`}>
                    <th>Employee {idx === 0 ? '' : idx + 1}:</th>
                    <td>{borrower.sunWestEmployee ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              <tr>
                <td colSpan={2} style={{ height: '20px' }}></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </EnvelopeWithBox>
  );
};

export default SystemFlags;
